/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { debounce } from '@ember/runloop';
import { task, timeout } from 'ember-concurrency';
import { isEmpty } from '@ember/utils';
import { htmlSafe } from '@ember/template';
import { hash } from 'rsvp';
import DS from 'ember-data';

import type * as ReactModule from 'react';
import type * as ReactDOMModule from 'react-dom';
import type * as ReactDOMClientModule from 'react-dom/client';

import config from 'cio/config/environment';
import { acceptedFileTypes } from 'cio/utils/images';
import { EmailPreview } from 'cio/utils/previews';
import EnvironmentService from 'cio/services/environment';
import AnalyticsService from 'cio/services/analytics';
import EventBusService from 'cio/services/event-bus';
import { Editor } from 'cio/services/editor';
import { EMAIL_COMPOSER_ROLLBACK } from 'cio/services/event-bus';
import {
  ComposerCampaignActionTemplate,
  ComposerNewsletterTemplate,
  ComposerTransactionMessageTemplate,
} from 'cio/utils/composer/composer-editable-entity';
import {
  VisualEditorPanelName as AnalyticsEditorPanelName,
  VisualEditorPropertiesPanelToggleMethod as AnalyticsPropertiesPanelToggleMethod,
} from 'cio/utils/analytics/transforms/message';
import { ComposerTab } from 'cio/components/action-composer-v2';
import { createAssetRecord, UploadStatus, FileUploadMetaSimple } from 'cio/utils/asset-library';
import isDestroyed from 'cio/utils/is-destroyed';

const DEFAULT_CODE_EDITOR_VALUE = `<x-base>
<x-section width="100%" padding="16px">
  <x-paragraph></x-paragraph>
</x-section>
</x-base>`;

const FULLSORY_IFRAME_SCRIPT = `
window['_fs_run_in_iframe'] = true;
window['_fs_host'] = 'fullstory.com';
window['_fs_script'] = 'edge.fullstory.com/s/fs.js';
window['_fs_org'] = 'MVQPJ';
window['_fs_namespace'] = 'FS';
(function(m,n,e,t,l,o,g,y){
if (e in m) {if(m.console && m.console.log) { m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].');} return;}
g=m[e]=function(a,b,s){g.q?g.q.push([a,b,s]):g._api(a,b,s);};g.q=[];
o=n.createElement(t);o.async=1;o.crossOrigin='anonymous';o.src='https://'+_fs_script;
y=n.getElementsByTagName(t)[0];y.parentNode.insertBefore(o,y);
g.identify=function(i,v,s){g(l,{uid:i},s);if(v)g(l,v,s)};g.setUserVars=function(v,s){g(l,v,s)};g.event=function(i,v,s){g('event',{n:i,p:v},s)};
g.anonymize=function(){g.identify(!!0)};
g.shutdown=function(){g("rec",!1)};g.restart=function(){g("rec",!0)};
g.log = function(a,b){g("log",[a,b])};
g.consent=function(a){g("consent",!arguments.length||a)};
g.identifyAccount=function(i,v){o='account';v=v||{};v.acctId=i;g(o,v)};
g.clearUserCookie=function(){};
g.setVars=function(n, p){g('setVars',[n,p]);};
g._w={};y='XMLHttpRequest';g._w[y]=m[y];y='fetch';g._w[y]=m[y];
if(m[y])m[y]=function(){return g._w[y].apply(this,arguments)};
g._v="1.3.0";
})(window,document,window['_fs_namespace'],'script','user');`;

const MAX_EMAIL_BODY_WIDTH = 940 + 16 * 2; // 16px for left and right padding
const MIN_CODE_EDITOR_PANEL_WIDTH = 400;
const LAYERS_INSERT_PANELS_WIDTH = 210;
const PROPERTIES_PANEL_WIDTH = 350;

interface EmailEditorArgs {
  editableEntity: ComposerCampaignActionTemplate | ComposerNewsletterTemplate | ComposerTransactionMessageTemplate;
  composerTab: ComposerTab;
  preview: EmailPreview | null;
  hasReadOnlyPermission: boolean;
}

interface CartaFileUpload {
  name: string;
  type: string;
  size: number;
  lastModified: number;
  data: string;
}

interface CartaComponentTarget {
  name: string;
  type: 'component';
  index: number;
}

export default class EmailEditor extends Component<EmailEditorArgs> {
  @service eventBus!: EventBusService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;

  // Flags to enable hidden features for development:
  readonly layersPanelEnabled = false;
  readonly propertiesPanelEnabled = false;
  readonly breadcrumbsEnabled = false;

  private React: typeof ReactModule | null = null;
  private ReactDOM: typeof ReactDOMModule | null = null;
  private ReactDOMClient: typeof ReactDOMClientModule | null = null;
  private Encompass: any | null = null;
  private createPortal:
    | ((element: React.ReactNode, container: Element | DocumentFragment | null) => React.ReactPortal)
    | null = null;
  private triggerReactDestroy: (() => unknown) | null = null;
  private isRevertingTemplate = false;

  private setCodeEditorPanelVisible: ((visible: boolean) => unknown) | null = null;
  private setInsertPanelVisible: ((visible: boolean) => unknown) | null = null;
  private setLayersPanelVisible: ((visible: boolean) => unknown) | null = null;
  private setPropertiesPanelVisible: ((visible: boolean) => unknown) | null = null;

  @tracked isCodeEditorPanelVisible = false;
  @tracked isInsertPanelVisible = false;
  @tracked isLayersPanelVisible = false;
  @tracked isPropertiesPanelVisible = false;

  @tracked encompassInstance: any | null = null;

  @tracked wrapperContainerElement: HTMLElement | null = null;
  @tracked wrapperContainerElementWidth = 0;
  private wrapperContainerResizeObserver: ResizeObserver | null = null;

  get codeEditorPanelWidth() {
    let otherPanelsWidth =
      (this.isInsertPanelVisible || this.isInsertPanelVisible ? LAYERS_INSERT_PANELS_WIDTH : 0) +
      (this.isPropertiesPanelVisible ? PROPERTIES_PANEL_WIDTH : 0);

    return Math.max(
      MIN_CODE_EDITOR_PANEL_WIDTH,
      this.wrapperContainerElementWidth - MAX_EMAIL_BODY_WIDTH - otherPanelsWidth,
    );
  }

  get allPanelsWidth() {
    return (
      (this.isCodeEditorPanelVisible ? this.codeEditorPanelWidth : 0) +
      (this.isInsertPanelVisible || this.isInsertPanelVisible ? LAYERS_INSERT_PANELS_WIDTH : 0) +
      (this.isPropertiesPanelVisible ? PROPERTIES_PANEL_WIDTH : 0)
    );
  }

  get editorParentMaxWidth() {
    return Math.min(this.allPanelsWidth + MAX_EMAIL_BODY_WIDTH, this.wrapperContainerElementWidth);
  }

  get visualizedEditorWidth() {
    return Math.min(this.editorParentMaxWidth, this.wrapperContainerElementWidth) - this.allPanelsWidth;
  }

  get codeEditorPanelStyles() {
    return this.getPanelStyles(this.isCodeEditorPanelVisible ? this.codeEditorPanelWidth : 0);
  }

  get layersAndInsertPanelStyles() {
    return this.getPanelStyles(this.isLayersPanelVisible || this.isInsertPanelVisible ? LAYERS_INSERT_PANELS_WIDTH : 0);
  }

  get visualizedEditorContainerStyles() {
    return this.getPanelStyles(this.visualizedEditorWidth);
  }

  get propertiesPanelStyles() {
    return this.getPanelStyles(this.isPropertiesPanelVisible ? PROPERTIES_PANEL_WIDTH : 0);
  }

  getPanelStyles(width: number) {
    return htmlSafe(`width:${width}px;`);
  }

  get visiblePanelsListForAnalytics(): AnalyticsEditorPanelName[] {
    let visiblePanels: AnalyticsEditorPanelName[] = [];

    if (this.isCodeEditorPanelVisible) {
      visiblePanels.push('code');
    }
    if (this.isInsertPanelVisible) {
      visiblePanels.push('insert');
    }
    if (this.isLayersPanelVisible) {
      visiblePanels.push('layers');
    }
    if (this.isPropertiesPanelVisible) {
      visiblePanels.push('properties');
    }

    return visiblePanels;
  }

  get cartaRootComponent(): CartaComponentTarget | null {
    return this.encompassInstance.visualEditor.state.get().tree?.children?.[0];
  }

  @action
  updateCartaComponentMeta() {
    let rootComponent = this.cartaRootComponent;
    if (rootComponent == null || this.args.editableEntity.template?.preheaderText == null) {
      return;
    }

    let definition = this.encompassInstance.visualEditor.actions.getComponentDefinition(rootComponent?.name);
    let hasPreheaderProperty = definition?.props?.fields?.some((f: any) => f?.name === 'preheader');
    if (hasPreheaderProperty !== true) {
      return;
    }

    this.encompassInstance.visualEditor.commands.setProps(rootComponent, {
      preheader: { value: this.args.editableEntity.template.preheaderText },
    });
  }

  @action
  toggleCodeEditorPanel() {
    this.trackCodeEditorPanelToggle();

    this.isCodeEditorPanelVisible = !this.isCodeEditorPanelVisible;
    this.setCodeEditorPanelVisible?.(this.isCodeEditorPanelVisible);

    if (this.isCodeEditorPanelVisible) {
      this.isInsertPanelVisible = false;
      this.setInsertPanelVisible?.(this.isInsertPanelVisible);

      this.isLayersPanelVisible = false;
      this.setLayersPanelVisible?.(this.isLayersPanelVisible);
    }
  }

  @action
  toggleInsertPanel() {
    this.trackInsertPanelToggle();
    this.isInsertPanelVisible = !this.isInsertPanelVisible;
    this.setInsertPanelVisible?.(this.isInsertPanelVisible);
  }

  @action
  toggleLayersPanel() {
    this.trackLayersPanelToggle();
    this.isLayersPanelVisible = !this.isLayersPanelVisible;
    this.setLayersPanelVisible?.(this.isLayersPanelVisible);
  }

  @action
  undoLastChange() {
    this.encompassInstance?.visualEditor.actions.undo();
  }

  @action
  redoLastChange() {
    this.encompassInstance?.visualEditor.actions.redo();
  }

  @action
  togglePropertiesPanel() {
    this.trackPropertiesPanelToggle('editor tool button');
    this.isPropertiesPanelVisible = !this.isPropertiesPanelVisible;
    this.setPropertiesPanelVisible?.(this.isPropertiesPanelVisible);
  }

  @action
  onWrapperContainerDidInsert(element: HTMLElement) {
    this.wrapperContainerElement = element;
    this.wrapperContainerElementWidth = element.clientWidth;

    this.wrapperContainerResizeObserver = new ResizeObserver(() =>
      requestAnimationFrame(this.handleWrapperContainerResize),
    );
    this.wrapperContainerResizeObserver.observe(this.wrapperContainerElement);
  }

  @action
  onVisualizedPreviewContainerDidInsert(element: HTMLElement) {
    this.subscribeToRollback();
    this.initializeEncompass(element);
  }

  willDestroy() {
    super.willDestroy();
    this.unsubscribeFromRollback();
    this.triggerReactDestroy?.();

    if (this.wrapperContainerResizeObserver) {
      this.wrapperContainerResizeObserver.disconnect();
      this.wrapperContainerResizeObserver = null;
    }
  }

  @action
  handleWrapperContainerResize() {
    if (isDestroyed(this) || this.wrapperContainerElement == null) {
      return;
    }
    this.wrapperContainerElementWidth = this.wrapperContainerElement.clientWidth;
  }

  @action
  subscribeToRollback() {
    this.eventBus.subscribe(EMAIL_COMPOSER_ROLLBACK, this, this.onTemplateRevert);
  }

  @action
  unsubscribeFromRollback() {
    this.eventBus.unsubscribe(EMAIL_COMPOSER_ROLLBACK, this, this.onTemplateRevert);
  }

  @action
  async onCodeEditorChange(source: string) {
    let { template } = this.args.editableEntity;

    if (
      this.isRevertingTemplate ||
      template.bodyJson === source ||
      (!this.isRevertingTemplate && isEmpty(template.bodyJson) && source === DEFAULT_CODE_EDITOR_VALUE)
    ) {
      return;
    }

    let evaluatedContent = await this.encompassInstance.visualEditor.actions.getHTML();
    template.body = evaluatedContent;
    template.bodyJson = source;

    let rootComponent = this.cartaRootComponent;
    if (rootComponent == null) {
      return;
    }
    let rootProps = await this.encompassInstance.visualEditor.actions.getComponentProps(rootComponent);
    let preheaderText = rootProps?.preheader?.value ?? '';
    if (preheaderText !== template.preheaderText) {
      template.preheaderText = preheaderText;
    }
  }

  @action
  onTemplateRevert() {
    let { template } = this.args.editableEntity;
    this.isRevertingTemplate = true;

    let codeEditorValue = isEmpty(template.bodyJson) ? DEFAULT_CODE_EDITOR_VALUE : template.bodyJson;
    this.encompassInstance.codeEditor.actions.updateValue(codeEditorValue);

    this.isRevertingTemplate = false;
  }

  /***************************************************************
   * Asset Library integration
   ***************************************************************/
  @tracked showAssetLibraryModal = false;
  @tracked showUploadPopup = false;
  @tracked uploads: FileUploadMetaSimple[] = [];

  private assetPathSelectedPromise?: Promise<unknown>;
  private assetPathSelectedResolve?: (path: string | null) => void;

  @action
  async getAssetPathFromAssetLibrary() {
    this.showAssetLibraryModal = true;

    this.assetPathSelectedPromise = new Promise((resolve) => {
      this.assetPathSelectedResolve = resolve;
    });

    return await this.assetPathSelectedPromise;
  }

  @action
  async uploadAssetAndReturnPath(file: CartaFileUpload) {
    let upload: FileUploadMetaSimple = { file, status: UploadStatus.Uploading };
    this.uploads = [...this.uploads, upload];
    this.showUploadPopup = true;

    try {
      if (!acceptedFileTypes.includes(file.type)) {
        throw new Error('File type is not supported.');
      }

      this.trackAssetUploadStarted(file);
      let asset = await createAssetRecord(file.name, file.data, this.store);
      this.updateUploads({ ...upload, status: UploadStatus.Success });
      this.maybeCloseUploadsPopup.perform();
      return asset.path;
    } catch (e: any) {
      let message = e?.errors?.[0]?.detail ?? e?.message ?? 'Image could not be uploaded';
      this.updateUploads({ ...upload, status: UploadStatus.Error, errorMessages: [{ message }] });
    }
  }

  @action
  resolveGettingAssetPath(path?: string) {
    this.showAssetLibraryModal = false;
    this.assetPathSelectedResolve?.(path ?? null);
    this.assetPathSelectedResolve = undefined;
  }

  private updateUploads(upload: FileUploadMetaSimple) {
    let uploads: FileUploadMetaSimple[] = [];
    for (let uploadMeta of this.uploads) {
      if (uploadMeta.file === upload.file) {
        let { file, status, errorMessages } = upload;
        uploads.push({ file, status, errorMessages });
      } else {
        uploads.push(uploadMeta);
      }
    }
    this.uploads = uploads;
  }

  private removeFinishedUploads() {
    this.uploads = this.uploads.filter(
      (upload) => upload.status !== UploadStatus.Success && upload.status !== UploadStatus.Error,
    );
  }

  @action
  onUploadPopupClose() {
    this.showUploadPopup = false;
    this.removeFinishedUploads();
  }

  maybeCloseUploadsPopup = task({ restartable: true }, async () => {
    let canAutoClose = !this.uploads.some(
      (upload) =>
        upload.status === UploadStatus.Pending ||
        upload.status === UploadStatus.Uploading ||
        upload.status === UploadStatus.Error,
    );
    if (canAutoClose) {
      await timeout(config.APP.longDelay);
      this.onUploadPopupClose();
    }
  });

  /***************************************************************
   * Analytics tracking
   ***************************************************************/
  get analyticsData() {
    return {
      ...this.args.editableEntity.analyticsData,
      editor: Editor.VISUAL,
      environmentId: this.environment.current.id,
    };
  }

  @action
  async trackEncompassEvent(eventName: string, data?: Record<string, any>) {
    switch (eventName) {
      case 'component_added':
      case 'component_duplicated': {
        this.analytics.track('message', 'component_added', {
          ...this.analyticsData,
          componentName: data?.componentName,
          presetLabel: isEmpty(data?.presetLabel) ? 'none' : data?.presetLabel,
          isDuplicated: eventName === 'component_duplicated',
          using: data?.using,
        });
        break;
      }

      case 'component_converted': {
        this.analytics.track('message', 'component_converted', {
          ...this.analyticsData,
          ...(data ? data : {}),
        });
        break;
      }
    }
  }

  @action
  trackAssetUploadStarted(file: CartaFileUpload) {
    this.analytics.track('asset_library', 'upload_image_started', {
      builderType: 'Visual',
      folderName: 'All Files',
      folderId: '',
      fileType: file.type,
      fileSize: file.size,
    });
  }

  // NOTE: We call panels toggle tracking methods before the actual toggling
  // because we want `visiblePanels` to reflect the state BEFORE the toggle.

  @action
  trackCodeEditorPanelToggle() {
    this.analytics.track('message', 'code_panel_toggled', {
      ...this.analyticsData,
      toggledOn: !this.isCodeEditorPanelVisible,
      visiblePanels: this.visiblePanelsListForAnalytics,
    });
  }

  @action
  trackInsertPanelToggle() {
    this.analytics.track('message', 'insert_panel_toggled', {
      ...this.analyticsData,
      toggledOn: !this.isInsertPanelVisible,
      visiblePanels: this.visiblePanelsListForAnalytics,
    });
  }

  @action
  trackLayersPanelToggle() {
    this.analytics.track('message', 'layers_panel_toggled', {
      ...this.analyticsData,
      toggledOn: !this.isLayersPanelVisible,
      visiblePanels: this.visiblePanelsListForAnalytics,
    });
  }

  @action
  trackPropertiesPanelToggle(toggleMethod: AnalyticsPropertiesPanelToggleMethod) {
    this.analytics.track('message', 'properties_panel_toggled', {
      ...this.analyticsData,
      toggledOn: !this.isPropertiesPanelVisible,
      visiblePanels: this.visiblePanelsListForAnalytics,
      toggleMethod,
    });
  }

  /***************************************************************
   * Encompass: ReactJS Visual Editor
   ***************************************************************/
  async initializeEncompass(element: HTMLElement) {
    let { React, ReactDOM, ReactDOMClient, Encompass } = await hash({
      React: import('react'),
      ReactDOM: import('react-dom'),
      ReactDOMClient: import('react-dom/client'),
      Encompass: import('@customerio/encompass'),
    });

    this.React = React;
    this.ReactDOM = ReactDOM;
    this.ReactDOMClient = ReactDOMClient;
    this.createPortal = ReactDOM.createPortal;
    this.Encompass = Encompass;

    let { template } = this.args.editableEntity;
    let codeEditorValue = isEmpty(template.bodyJson) ? DEFAULT_CODE_EDITOR_VALUE : template.bodyJson;

    this.encompassInstance = this.Encompass.createEncompass({
      codeEditor: {
        path: 'index.html',
        value: codeEditorValue,
        readOnly: false,
      },
      visualEditor: {
        autofocus: true,
        onSelectMedia: this.getAssetPathFromAssetLibrary,
        onUploadMedia: this.uploadAssetAndReturnPath,
        onTrack: this.trackEncompassEvent,
        globalComponents: this.Encompass.components,
        defaultProseComponent: 'x-paragraph',
        injectScripts: [FULLSORY_IFRAME_SCRIPT],
      },
    });

    this.encompassInstance.visualEditor.__visualized.on('sourceUpdate', ({ source }: { source: string }) => {
      this.onCodeEditorChange(source);
    });

    // manually set configuration used by monaco-editor since:
    // * @monaco-editor/react doesn't initialize fully when editor imported as a module
    // * node_modules/monaco-editor/esm/vs/base/common/worker/simpleWorker.js:249 depends on this
    let monacoLoaderObj: any = window.require;
    if (monacoLoaderObj.getConfig == null) {
      monacoLoaderObj.getConfig = () => ({
        catchError: false,
        isBuild: false,
        config: {},
      });
    }

    this.ReactDOMClient?.createRoot(element).render(this.visualEditorElement());
  }

  visualEditorElement() {
    if (this.React == null || this.ReactDOM == null || this.createPortal == null || this.Encompass == null) {
      return null;
    }

    let { EncompassProvider, EncompassTheme, EncompassLayout, EncompassCodeEditor, EncompassVisualEditor } =
      this.Encompass;

    let { React, createPortal } = this;
    let { useState } = React;

    let App = () => {
      let [shouldRender, setShouldRender] = useState(true);
      let [showCodeEditor, setShowCodeEditor] = useState(this.isCodeEditorPanelVisible);
      let [showInsertMenu, setShowInsertMenu] = useState(this.isInsertPanelVisible);
      let [showLayers, setShowLayers] = useState(this.isInsertPanelVisible);
      let [showPropertiesMenu, setShowPropertiesMenu] = useState(this.isPropertiesPanelVisible);

      this.setCodeEditorPanelVisible = setShowCodeEditor;
      this.setInsertPanelVisible = setShowInsertMenu;
      this.setLayersPanelVisible = setShowLayers;
      this.setPropertiesPanelVisible = (visible: boolean) =>
        visible
          ? this.encompassInstance.visualEditor.actions.showFullPropertiesMenu()
          : this.encompassInstance.visualEditor.actions.hideFullPropertiesMenu();

      React.useEffect(() => {
        let unsubscribe = this.encompassInstance.visualEditor.state.listen((state: any) => {
          // Properties panel visibility can be toggled by Encompass.
          let propertiesPanelVisible = state?.fullPropertiesMenu?.isVisible === true;
          if (this.isPropertiesPanelVisible !== propertiesPanelVisible) {
            this.trackPropertiesPanelToggle('bubble menu');
            this.isPropertiesPanelVisible = propertiesPanelVisible;
          }
          setShowPropertiesMenu(propertiesPanelVisible);
        });
        return () => {
          unsubscribe();
        };
      }, []);

      React.useEffect(() => {
        this.isCodeEditorPanelVisible = showCodeEditor;
        this.isInsertPanelVisible = showInsertMenu;
        this.isLayersPanelVisible = showLayers;
        this.isPropertiesPanelVisible = showPropertiesMenu;
      }, [showCodeEditor, showInsertMenu, showLayers, showPropertiesMenu]);

      this.triggerReactDestroy = () => {
        setShouldRender(false);
      };

      let codePanelContainer = document.getElementById('code-panel');
      let codePanelComponent = codePanelContainer
        ? createPortal(
            React.createElement(EncompassCodeEditor, {
              onInput: (value: string) => {
                debounce(this, this.encompassInstance.visualEditor.commands.setContent, value, 300);
              },
            }),
            codePanelContainer,
          )
        : null;

      let insertPanelContainer = document.getElementById('insert-panel');
      let insertPanelComponent = insertPanelContainer
        ? createPortal(React.createElement(EncompassVisualEditor.InsertMenu, null), insertPanelContainer)
        : null;

      let layersPanelContainer = document.getElementById('layers-panel');
      let layersPanelComponent = layersPanelContainer
        ? createPortal(React.createElement(EncompassVisualEditor.Layers, null), layersPanelContainer)
        : null;

      let propertiesPanelContainer = document.getElementById('properties-panel');
      let propertiesPanelComponent = propertiesPanelContainer
        ? createPortal(React.createElement(EncompassVisualEditor.FullPropertiesMenu, null), propertiesPanelContainer)
        : null;

      let contextBreadcrumbsContainer = document.getElementById('context-breadcrumbs');
      let contextBreadcrumbsComponent = contextBreadcrumbsContainer
        ? createPortal(React.createElement(EncompassVisualEditor.ContextBar, null), contextBreadcrumbsContainer)
        : null;

      return React.createElement(
        'div',
        { className: 'h-full w-full grandchild-h-full' },
        shouldRender &&
          React.createElement(
            EncompassProvider,
            { encompass: this.encompassInstance },
            React.createElement(
              EncompassTheme,
              null,
              React.createElement(
                EncompassLayout.Root,
                null,
                React.createElement(EncompassVisualEditor.View, null),
                showInsertMenu && insertPanelComponent,
                this.layersPanelEnabled && showLayers && layersPanelComponent,
                showCodeEditor && codePanelComponent,
                this.propertiesPanelEnabled && showPropertiesMenu && propertiesPanelComponent,
                React.createElement(EncompassVisualEditor.CorePropertiesMenu, null),
                contextBreadcrumbsComponent,
              ),
            ),
          ),
      );
    };

    return React.createElement(App, null);
  }
}
